<!-- Small colored icon overlay for BPMN layers (e.g. compliancy, entity refs). -->
<!-- Looks up its tooltip content from the process-elements store reactively. -->
<template>
    <font-awesome-icon
        v-tooltip.top="tooltip"
        class="layer-dot"
        :icon="icon"
        :style="{ color }"
    />
</template>

<script setup lang="ts">
import { computed } from "vue";

import { decodeArtefactPreview } from "@/lib/artefacts/preview";
import { ProcessElementType, ProcessTask } from "@/lib/openapi/gen";
import { useApplicationsManager } from "@/stores/applications-store";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { useProcessElementsManager } from "@/stores/process-elements-store.js";

type LayerMode =
    | "compliancy"
    | "linkedEntities"
    | "documents"
    | "description"
    | "applications";

const props = defineProps<{
    color: string;
    icon: string | string[];
    elementId: string;
    processElementId?: string;
    mode: LayerMode;
}>();

const { processElements } = useProcessElementsManager();
const { entities } = useBusinessEntitiesManager();
const { applications } = useApplicationsManager();

const processElement = computed(() =>
    processElements.value?.find(
        (p) =>
            p.identity === props.processElementId ||
            p.bpmn_id === props.elementId
    )
);

const tooltip = computed(() => {
    if (props.mode === "linkedEntities") {
        const refs = processElement.value?.entity_refs ?? [];
        if (!refs.length) return "";
        const sentences = refs.map((r) => {
            const name =
                entities.value?.find((e) => e.identity === r.entity_id)?.name ??
                r.entity_id;
            return `${r.label} ${name}`;
        });
        return `${sentences.join("\n")}`;
    }

    if (
        processElement.value?.type === ProcessElementType.TASK &&
        props.mode === "compliancy"
    ) {
        return decodeArtefactPreview(
            (processElement.value as ProcessTask).evidence_instructions?.preview
        );
    }

    if (props.mode === "documents") {
        const count = processElement.value?.document_count ?? 0;
        if (!count) return "";
        return count === 1
            ? "1 document attached"
            : `${count} documents attached`;
    }

    if (props.mode === "description") {
        return processElement.value?.short_description ?? "";
    }

    if (props.mode === "applications") {
        const taskId = processElement.value?.identity;
        if (!taskId) return "";
        const linked = applications.value.filter((a) =>
            a.linked_process_task_ids?.includes(taskId)
        );
        if (!linked.length) return "";
        return linked.map((a) => a.name).join("\n");
    }

    return "";
});
</script>

<style scoped>
.layer-dot {
    min-width: 8px;
    height: 8px;
    margin-right: 2px;
    transform: translate(2px, -2px);
    cursor: help;
    pointer-events: auto;
}
</style>
